import { useEffect, useRef, useState } from "react";

/**
 * Subscribe to the DUX live-update SSE stream.
 *
 * ``topics`` are server-side topic strings (e.g. ``"contacts:sarah-mueller-a3f7"``).
 * ``onEvent`` is called per frame. The hook also exposes a simple status
 * machine for UI indicators.
 */
export type LiveEvent = {
  table: string;
  id: string;
  op: string;
  contact_id?: string;
  campaign_slug?: string;
};

export type LiveStatus = "connecting" | "open" | "closed";

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

export function useLiveUpdates(
  topics: ReadonlyArray<string>,
  onEvent: (ev: LiveEvent) => void,
): { status: LiveStatus; retryNow: () => void } {
  const [status, setStatus] = useState<LiveStatus>("connecting");
  const onEventRef = useRef(onEvent);
  const retryNowRef = useRef<() => void>(() => {});
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);
  const topicKey = topics.slice().sort().join("|");

  useEffect(() => {
    if (topics.length === 0) {
      setStatus("closed");
      return;
    }
    let closed = false;
    let attempt = 0;
    let es: EventSource | null = null;
    let retryTimer: number | null = null;

    const qs = topics
      .map((t) => `topic=${encodeURIComponent(t)}`)
      .join("&");
    const url = `/api/v1/events/stream?${qs}`;

    const connect = () => {
      setStatus("connecting");
      es = new EventSource(url, { withCredentials: true });
      es.onopen = () => {
        attempt = 0;
        setStatus("open");
      };
      const handleFrame = (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data) as LiveEvent;
          onEventRef.current(data);
        } catch {
          // Ignore malformed frames; the server guarantees JSON.
        }
      };
      es.onmessage = handleFrame;
      for (const op of ["INSERT", "UPDATE", "DELETE", "RESYNC"]) {
        es.addEventListener(op, handleFrame as EventListener);
      }
      es.onerror = () => {
        if (closed) return;
        setStatus("connecting");
        es?.close();
        es = null;
        const delay = Math.min(
          MAX_BACKOFF_MS,
          BASE_BACKOFF_MS * Math.pow(2, attempt),
        );
        attempt += 1;
        retryTimer = window.setTimeout(connect, delay);
      };
    };

    retryNowRef.current = () => {
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      es?.close();
      attempt = 0;
      connect();
    };

    connect();

    return () => {
      closed = true;
      setStatus("closed");
      if (retryTimer) window.clearTimeout(retryTimer);
      es?.close();
    };
  }, [topicKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, retryNow: () => retryNowRef.current() };
}
